import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  setMessages,
  setWithMessagesConversations,
  updateMessage,
  promoteConversation,
} from "../Slices/ChatSlice";
import { callApi } from "../common/Apicall";

const useSendMessage = () => {
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const URL = process.env.REACT_APP_URL;
  const connection = useSelector((state) => state.socket.socketConnection);
  const loggedInUser = useSelector(
    (state) => state?.data?.userInfo?.data?.data
  );
  const senderId = loggedInUser?.UserId;
  const selectedConversationData = useSelector((state) => state.conversation);
  const selectedConversationType = selectedConversationData?.conversationType;
  const receiverId = selectedConversationData?.selectedConversation?.value;
  const messages = useSelector((state) => state.conversation?.messages || []);
  const conversationsList = useSelector(
    (state) => state.conversation?.withMessagesConversations || []
  );
  const dispatch = useDispatch();

  const sendmessage = async (
    formData,
    isResend = false,
    failedMessageId = null
  ) => {
    setLoading(true);
    setIsSuccess(false);

    const tempMessage = {
      ...formData,
      content: formData?.Content,
      fileName: formData?.File?.name,
      attachedFile: formData?.File,
      repliedMessage: formData?.ReplyingTo,
      repliedMessageId: formData?.RepliedMessageId,
      senderId,
      timestamp: true,
      createdAt: new Date().toISOString(),
      id: failedMessageId || Math.random().toString(36).substring(7),
      isFailed: false,
    };

    // Add or update temp message in Redux
    if (isResend) {
      const updatedMessages = messages.map((msg) =>
        msg.id === failedMessageId ? tempMessage : msg
      );
      dispatch(setMessages(updatedMessages));
    } else {
      dispatch(setMessages([...messages, tempMessage]));
    }

    try {
      // ✅ Send message through API
      let res;
      if (selectedConversationType === "group") {
        res = await callApi(
          `${URL}/GroupChat/Send-Message/${receiverId}?senderId=${senderId}`,
          "post",
          formData,
          true
        );
      } else {
        res = await callApi(
          `${URL}/Chats/send/${receiverId}?senderId=${senderId}`,
          "post",
          formData,
          true
        );
      }

      if (res.error) throw new Error(res.error);

      // ✅ Update the message in Redux as successful
      const newMessages = isResend
        ? messages.map((msg) =>
            msg.id === failedMessageId
              ? { ...res, isFailed: false, timestamp: false }
              : msg
          )
        : [...messages, { ...res, isFailed: false, timestamp: false }];

      dispatch(setMessages(newMessages));
      setIsSuccess(true);

      const notificationMessage = {
        ...res,
        fullName: `${loggedInUser?.FirstName} ${loggedInUser?.LastName}`,
        groupName: selectedConversationData?.selectedConversation?.label,
        profilePic: loggedInUser?.ProfilePicture,
        messageType: selectedConversationType,
        groupParticipants:
          selectedConversationData?.selectedConversation?.participants,
      };

      // ✅ Try sending via SignalR — but if it fails, do NOT mark message failed
      try {
        if (connection?.state === "Connected") {
          await connection.invoke(
            "SendMessageToUser",
            String(receiverId),
            notificationMessage
          );
        } else {
          console.warn(
            "SignalR connection is not active — skipping realtime send"
          );
        }
      } catch (signalError) {
        console.warn(
          "SignalR send failed, but message sent to server:",
          signalError
        );
        // Do not mark failed since API already succeeded
      }

      // ✅ Reorder the sidebar locally instead of refetching the whole
      // `users-with-messages` list — sending only moves this conversation to
      // the top, which the reducer can do without a network round trip. A
      // conversation that isn't in the list yet (a chat started from user
      // search) has nothing local to update, so that case still refetches.
      if (selectedConversationType === "simple") {
        const isKnownConversation = conversationsList.some(
          (conversation) =>
            conversation?.value != null &&
            String(conversation.value) === String(receiverId)
        );

        if (isKnownConversation) {
          dispatch(
            promoteConversation({
              conversationId: receiverId,
              conversationType: "simple",
            })
          );
        } else {
          await fetchConversations();
        }
      }
    } catch (error) {
      // ❌ Only API failures reach here
      console.error("Send message failed:", error);
      toast.error(error.message || "Failed to send the message!");

      const failedMessage = {
        ...tempMessage,
        timestamp: false,
        isFailed: true,
      };

      const updatedMessages = isResend
        ? messages.map((msg) =>
            msg.id === failedMessageId ? failedMessage : msg
          )
        : [...messages, failedMessage];

      dispatch(setMessages(updatedMessages));
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await callApi(`${URL}/Chats/users-with-messages`, "GET");
      if (res.error) throw new Error(res.error);
      dispatch(setWithMessagesConversations(res));
    } catch (error) {
      toast.error(`Failed to fetch conversations: ${error.message}`);
    }
  };

  const editMessage = async (formData, editingMessage) => {
    setLoading(true);
    setIsSuccess(false);

    try {
      let res;
      if (selectedConversationType === "group") {
        res = await callApi(
          `${URL}/GroupChat/Edit-Message/${formData.MessageId}`,
          "put",
          formData,
          true
        );
      } else {
        res = await callApi(
          `${URL}/Chats/Edit-Message/${formData.MessageId}`,
          "put",
          formData,
          true
        );
      }

      if (res.error) throw new Error(res.error);

      // Update Redux
      dispatch(
        updateMessage({
          messageId: formData.MessageId,
          updatedProperties: {
            content: formData.Content,
            fileName: formData?.File?.name,
            attachedFile: formData?.File,
            isEdited: true,
            isRead: false,
            seenUsers: [],
          },
        })
      );

      const updatedMessage = {
        ...editingMessage,
        content: formData.Content,
        fileName: formData?.File?.name,
        attachedFile: formData?.File,
        isEdited: true,
        messageId: editingMessage?.id,
      };

      setIsSuccess(true);

      // Notify via SignalR (optional)
      try {
        if (connection?.state === "Connected") {
          await connection.invoke(
            "UpdateMessage",
            String(receiverId),
            updatedMessage
          );
        } else {
          console.warn("SignalR not connected — skipping edit notification");
        }
      } catch (signalError) {
        console.warn("SignalR edit notify failed:", signalError);
      }
    } catch (error) {
      toast.error(error.message || "Failed to edit the message!");
    } finally {
      setLoading(false);
    }
  };

  return { loading, isSuccess, sendmessage, editMessage };
};

export default useSendMessage;
